import {
  ROLE_LABELS,
  USER_COLORS,
  formatDateTime,
  generateTempPassword,
  plural,
  suggestLogin,
  userCreateSchema,
  userUpdateSchema,
  type Role,
  type UserAdminView,
  type UserColor,
} from '@kominiarz/shared';
import { Dices, KeyRound, Power, Save, UserPlus } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/useAuth';
import { Alert } from '../../components/Alert';
import { Button, ButtonLink } from '../../components/Button';
import { useConfirm } from '../../components/Confirm';
import { Dialog } from '../../components/Dialog';
import { ChoiceGroup } from '../../components/form/ChoiceGroup';
import { ColorPicker } from '../../components/form/ColorPicker';
import { TextInput } from '../../components/form/TextInput';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { useToast } from '../../components/Toast';
import { ApiError, errorMessage } from '../../lib/api';
import {
  useAdminUsers,
  useCreateUser,
  useResetPassword,
  useSetActive,
  useUpdateUser,
  useUserDetail,
} from '../../lib/usersApi';
import { UserStatus } from './UserStatus';
import styles from './users.module.css';

const ROLE_CHOICES = (['worker', 'admin'] as const).map((r) => ({ value: r, label: ROLE_LABELS[r] }));

function issuesToFields(issues: Array<{ path: PropertyKey[]; message: string }>) {
  const fields: Record<string, string> = {};
  for (const i of issues) fields[String(i.path[0])] ??= i.message;
  return fields;
}

/** Colours of other active people, so the manager can pick a distinct one. */
function useTakenColors(exceptId?: number) {
  const users = useAdminUsers();
  return useMemo(
    () => new Set((users.data ?? []).filter((u) => u.isActive && u.id !== exceptId).map((u) => u.color)),
    [users.data, exceptId],
  );
}

function TempPasswordField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div className={styles.passwordRow}>
      <TextInput
        label="Hasło tymczasowe"
        hint="Przekaż je tej osobie. Przy pierwszym logowaniu ustawi własne hasło."
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
      />
      <div>
        <Button variant="ghost" icon={<Dices aria-hidden="true" />} onClick={() => onChange(generateTempPassword())}>
          Wylosuj inne
        </Button>
      </div>
    </div>
  );
}

function Credentials({ login, password }: { login: string; password: string }) {
  return (
    <dl className={styles.credentials}>
      <dt>Login</dt>
      <dd>{login}</dd>
      <dt>Hasło tymczasowe</dt>
      <dd>{password}</dd>
    </dl>
  );
}

// ---------------------------------------------------------------------------

export function NewUserPage() {
  const navigate = useNavigate();
  const create = useCreateUser();
  const taken = useTakenColors();

  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [loginTouched, setLoginTouched] = useState(false);
  const [role, setRole] = useState<Role>('worker');
  const [color, setColor] = useState<UserColor | null>(null);
  const [password, setPassword] = useState(generateTempPassword);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<{ user: UserAdminView; password: string } | null>(null);

  const effectiveColor: UserColor = color ?? USER_COLORS.find((c) => !taken.has(c)) ?? 'blue';

  async function submit(e: FormEvent) {
    e.preventDefault();
    const body = { name, login, role, color: effectiveColor, password };
    const parsed = userCreateSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(issuesToFields(parsed.error.issues));
      return;
    }
    setErrors({});
    try {
      const res = await create.mutateAsync(parsed.data);
      setCreated({ user: res.user, password: parsed.data.password });
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fields);
    }
  }

  if (created) {
    return (
      <>
        <PageHeader title="Konto utworzone" back="/uzytkownicy" />
        <PageBody>
          <Section title={created.user.name} description="Przekaż te dane. Przy pierwszym logowaniu trzeba ustawić własne hasło.">
            <Credentials login={created.user.login} password={created.password} />
            <div className={styles.actions}>
              <ButtonLink to="/uzytkownicy" variant="primary">
                Gotowe
              </ButtonLink>
              <Button
                variant="secondary"
                icon={<UserPlus aria-hidden="true" />}
                onClick={() => {
                  setCreated(null);
                  setName('');
                  setLogin('');
                  setLoginTouched(false);
                  setColor(null);
                  setPassword(generateTempPassword());
                }}
              >
                Dodaj kolejną osobę
              </Button>
            </div>
          </Section>
        </PageBody>
      </>
    );
  }

  const showAlert = create.error && !(create.error instanceof ApiError && Object.keys(create.error.fields).length);

  return (
    <>
      <PageHeader title="Nowy użytkownik" back="/uzytkownicy" />
      <PageBody>
        <form className={styles.form} onSubmit={submit} noValidate>
          <Section title="Dane osoby">
            <TextInput
              label="Imię i nazwisko"
              autoComplete="off"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!loginTouched) setLogin(suggestLogin(e.target.value));
              }}
              error={errors.name}
            />
            <TextInput
              label="Login"
              hint="Małe litery bez polskich znaków, np. jan.kowalski."
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={login}
              onChange={(e) => {
                setLogin(e.target.value);
                setLoginTouched(true);
              }}
              error={errors.login}
            />
            <ChoiceGroup<Role> legend="Rola" value={role} options={ROLE_CHOICES} columns={2} onChange={setRole} error={errors.role} />
            <ColorPicker legend="Kolor wizyt w kalendarzu" value={effectiveColor} onChange={setColor} taken={taken} />
            <TempPasswordField value={password} onChange={setPassword} error={errors.password} />
          </Section>
          {showAlert && <Alert kind="error">{errorMessage(create.error)}</Alert>}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" size="lg" icon={<UserPlus aria-hidden="true" />} disabled={create.isPending}>
              {create.isPending ? 'Zapisywanie…' : 'Dodaj użytkownika'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate('/uzytkownicy')}>
              Anuluj
            </Button>
          </div>
        </form>
      </PageBody>
    </>
  );
}

// ---------------------------------------------------------------------------

export function EditUserPage() {
  const id = Number(useParams().id);
  const detail = useUserDetail(id);

  if (detail.isPending) {
    return (
      <>
        <PageHeader title="Użytkownik" back="/uzytkownicy" />
        <PageBody>
          <Spinner />
        </PageBody>
      </>
    );
  }
  if (detail.isError) {
    return (
      <>
        <PageHeader title="Użytkownik" back="/uzytkownicy" />
        <PageBody>
          <Alert kind="error">{errorMessage(detail.error)}</Alert>
        </PageBody>
      </>
    );
  }
  // Keyed by id: switching to another person starts the form from their saved values.
  return <EditUserForm key={detail.data.id} user={detail.data} />;
}

function EditUserForm({ user }: { user: NonNullable<ReturnType<typeof useUserDetail>['data']> }) {
  const me = useCurrentUser();
  const toast = useToast();
  const confirm = useConfirm();
  const update = useUpdateUser(user.id);
  const setActive = useSetActive(user.id);
  const taken = useTakenColors(user.id);
  const isMe = me.id === user.id;

  const [name, setName] = useState(user.name);
  const [login, setLogin] = useState(user.login);
  const [role, setRole] = useState<Role>(user.role);
  const [color, setColor] = useState<UserColor>(user.color);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resetOpen, setResetOpen] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = userUpdateSchema.safeParse({ name, login, role, color });
    if (!parsed.success) {
      setErrors(issuesToFields(parsed.error.issues));
      return;
    }
    setErrors({});
    try {
      await update.mutateAsync(parsed.data);
      toast.success('Zapisano zmiany');
    } catch (err) {
      if (err instanceof ApiError) setErrors(err.fields);
    }
  }

  async function toggleActive() {
    if (user.isActive) {
      const visits =
        user.upcomingVisitCount > 0
          ? ` Ma ${plural(user.upcomingVisitCount, 'zaplanowaną wizytę', 'zaplanowane wizyty', 'zaplanowanych wizyt')} – przypisz je komuś innemu.`
          : '';
      const ok = await confirm({
        title: 'Wyłączyć konto?',
        message: `${user.name} straci dostęp do aplikacji i zniknie z list wyboru. Historia wizyt zostaje.${visits}`,
        confirmLabel: 'Wyłącz konto',
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await setActive.mutateAsync(!user.isActive);
      toast.success(user.isActive ? 'Konto wyłączone' : 'Konto włączone');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  }

  const showAlert = update.error && !(update.error instanceof ApiError && Object.keys(update.error.fields).length);

  return (
    <>
      <PageHeader title={user.name} subtitle={ROLE_LABELS[user.role]} back="/uzytkownicy" />
      <PageBody>
        <div className={styles.layout}>
          <form className={styles.form} onSubmit={submit} noValidate>
            <Section title="Dane osoby">
              <TextInput label="Imię i nazwisko" value={name} onChange={(e) => setName(e.target.value)} error={errors.name} />
              <TextInput
                label="Login"
                autoCapitalize="none"
                spellCheck={false}
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                error={errors.login}
              />
              <ChoiceGroup<Role>
                legend="Rola"
                hint={isMe ? 'Własnej roli nie możesz zmienić.' : undefined}
                value={role}
                options={ROLE_CHOICES}
                columns={2}
                onChange={setRole}
                error={errors.role}
                disabled={isMe}
              />
              <ColorPicker legend="Kolor wizyt w kalendarzu" value={color} onChange={setColor} taken={taken} />
            </Section>
            {showAlert && <Alert kind="error">{errorMessage(update.error)}</Alert>}
            <div className={styles.actions}>
              <Button type="submit" variant="primary" size="lg" icon={<Save aria-hidden="true" />} disabled={update.isPending}>
                {update.isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
              </Button>
              <ButtonLink to="/uzytkownicy" variant="secondary" size="lg">
                Wróć do listy
              </ButtonLink>
            </div>
          </form>

          <div className={styles.form}>
            <Section title="Konto">
              <div className={styles.actions}>
                <UserStatus user={user} />
              </div>
              <div className={styles.facts}>
                <span>Ostatnie logowanie: {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'jeszcze nie'}</span>
                <span>Zaplanowane wizyty: {user.upcomingVisitCount}</span>
              </div>
              {isMe ? (
                <p className={styles.facts}>Własne hasło zmienisz w Ustawieniach.</p>
              ) : (
                <div className={styles.actions}>
                  <Button variant="secondary" icon={<KeyRound aria-hidden="true" />} onClick={() => setResetOpen(true)}>
                    Ustaw hasło tymczasowe
                  </Button>
                  <Button
                    variant={user.isActive ? 'danger' : 'success'}
                    icon={<Power aria-hidden="true" />}
                    onClick={toggleActive}
                    disabled={setActive.isPending}
                  >
                    {user.isActive ? 'Wyłącz konto' : 'Włącz konto'}
                  </Button>
                </div>
              )}
            </Section>
          </div>
        </div>
        <ResetPasswordDialog user={user} open={resetOpen} onClose={() => setResetOpen(false)} />
      </PageBody>
    </>
  );
}

function ResetPasswordDialog({ user, open, onClose }: { user: UserAdminView; open: boolean; onClose: () => void }) {
  const reset = useResetPassword(user.id);
  const [password, setPassword] = useState(generateTempPassword);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);

  function close() {
    onClose();
    // Prepare a fresh suggestion for the next time the dialog opens.
    setDone(false);
    setError(undefined);
    setPassword(generateTempPassword());
    reset.reset();
  }

  async function submit() {
    try {
      await reset.mutateAsync(password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError && err.fields.password ? err.fields.password : errorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      title={done ? 'Hasło ustawione' : 'Hasło tymczasowe'}
      onClose={close}
      actions={
        done ? (
          <Button variant="primary" size="lg" onClick={close}>
            Gotowe
          </Button>
        ) : (
          <>
            <Button variant="primary" size="lg" onClick={submit} disabled={reset.isPending}>
              {reset.isPending ? 'Zapisywanie…' : 'Ustaw hasło'}
            </Button>
            <Button variant="secondary" size="lg" onClick={close}>
              Anuluj
            </Button>
          </>
        )
      }
    >
      {done ? (
        <>
          <p>Przekaż te dane. {user.name} ustawi własne hasło przy logowaniu.</p>
          <Credentials login={user.login} password={password} />
        </>
      ) : (
        <>
          <p>
            To konto zostanie wylogowane na wszystkich urządzeniach. Jeśli było zablokowane po nieudanych próbach
            logowania, zostanie odblokowane.
          </p>
          <TempPasswordField value={password} onChange={setPassword} error={error} />
        </>
      )}
    </Dialog>
  );
}
